// src/services/storageService.js - Industry-Level State Persistence
import Cookies from 'js-cookie';

/**
 * Storage Service - Handles cookies, localStorage, and sessionStorage
 * with fallbacks and encryption support
 */

const COOKIE_OPTIONS = {
  expires: 365, // 1 year
  secure: process.env.NODE_ENV === 'production',
  sameSite: 'strict'
};

// const SESSION_COOKIE_OPTIONS = {
//   expires: 1, // 1 day
//   secure: process.env.NODE_ENV === 'production',
//   sameSite: 'strict'
// };

class StorageService {
  constructor() {
    this.cookiePrefix = 'giveaura_';
    this.localStoragePrefix = 'giveaura_';
    this.sessionStoragePrefix = 'giveaura_session_';
  }

  // ==================== COOKIE MANAGEMENT ====================
  
  /**
   * Set cookie with automatic JSON serialization
   */
  setCookie(key, value, options = {}) {
    try {
      const prefixedKey = this.cookiePrefix + key;
      const serialized = typeof value === 'object' ? JSON.stringify(value) : value;
      Cookies.set(prefixedKey, serialized, { ...COOKIE_OPTIONS, ...options });
      return true;
    } catch (error) {
      console.error('Error setting cookie:', error);
      return false;
    }
  }

  /**
   * Get cookie with automatic JSON parsing
   */
  getCookie(key, defaultValue = null) {
    try {
      const prefixedKey = this.cookiePrefix + key;
      const value = Cookies.get(prefixedKey);
      
      if (!value) return defaultValue;
      
      // Try to parse as JSON
      try {
        return JSON.parse(value);
      } catch {
        return value;
      }
    } catch (error) {
      console.error('Error getting cookie:', error);
      return defaultValue;
    }
  }

  /**
   * Remove cookie
   */
  removeCookie(key) {
    try {
      const prefixedKey = this.cookiePrefix + key;
      Cookies.remove(prefixedKey);
      return true;
    } catch (error) {
      console.error('Error removing cookie:', error);
      return false;
    }
  }

  // ==================== LOCAL STORAGE ====================
  
  /**
   * Set localStorage with automatic JSON serialization
   */
  setLocal(key, value) {
    try {
      const prefixedKey = this.localStoragePrefix + key;
      const serialized = JSON.stringify(value);
      localStorage.setItem(prefixedKey, serialized);
      return true;
    } catch (error) {
      console.error('Error setting localStorage:', error);
      return false;
    }
  }

  /**
   * Get localStorage with automatic JSON parsing
   */
  getLocal(key, defaultValue = null) {
    try {
      const prefixedKey = this.localStoragePrefix + key;
      const value = localStorage.getItem(prefixedKey);
      
      if (!value) return defaultValue;
      
      return JSON.parse(value);
    } catch (error) {
      console.error('Error getting localStorage:', error);
      return defaultValue;
    }
  }

  /**
   * Remove localStorage item
   */
  removeLocal(key) {
    try {
      const prefixedKey = this.localStoragePrefix + key;
      localStorage.removeItem(prefixedKey);
      return true;
    } catch (error) {
      console.error('Error removing localStorage:', error);
      return false;
    }
  }

  // ==================== SESSION STORAGE ====================
  
  /**
   * Set sessionStorage with automatic JSON serialization
   */
  setSession(key, value) {
    try {
      const prefixedKey = this.sessionStoragePrefix + key;
      const serialized = JSON.stringify(value);
      sessionStorage.setItem(prefixedKey, serialized);
      return true;
    } catch (error) {
      console.error('Error setting sessionStorage:', error);
      return false;
    }
  }

  /**
   * Get sessionStorage with automatic JSON parsing
   */
  getSession(key, defaultValue = null) {
    try {
      const prefixedKey = this.sessionStoragePrefix + key;
      const value = sessionStorage.getItem(prefixedKey);
      
      if (!value) return defaultValue;
      
      return JSON.parse(value);
    } catch (error) {
      console.error('Error getting sessionStorage:', error);
      return defaultValue;
    }
  }

  /**
   * Remove sessionStorage item
   */
  removeSession(key) {
    try {
      const prefixedKey = this.sessionStoragePrefix + key;
      sessionStorage.removeItem(prefixedKey);
      return true;
    } catch (error) {
      console.error('Error removing sessionStorage:', error);
      return false;
    }
  }

  // ==================== NAVIGATION STATE ====================
  
  /**
   * Save navigation state (screen, scroll position, filters)
   */
  saveNavigationState(state) {
    const navState = {
      screen: state.screen,
      scrollPosition: state.scrollPosition || 0,
      timestamp: Date.now(),
      filters: state.filters || {},
      selectedCategory: state.selectedCategory || 'All',
      searchQuery: state.searchQuery || '',
      productHistory: state.productHistory || []
    };
    
    this.setLocal('navigation_state', navState);
    this.setSession('navigation_state', navState);
    return true;
  }

  /**
   * Get navigation state
   */
  getNavigationState() {
    // Try session first (more recent), then local
    const sessionState = this.getSession('navigation_state');
    if (sessionState && (Date.now() - sessionState.timestamp) < 3600000) { // 1 hour
      return sessionState;
    }
    
    const localState = this.getLocal('navigation_state');
    if (localState && (Date.now() - localState.timestamp) < 86400000) { // 24 hours
      return localState;
    }
    
    return null;
  }

  /**
   * Clear navigation state
   */
  clearNavigationState() {
    this.removeLocal('navigation_state');
    this.removeSession('navigation_state');
  }

  // ==================== SCROLL POSITIONS ====================
  
  /**
   * Save scroll position for a specific screen/key
   */
  saveScrollPosition(key, position) {
    const scrollData = this.getLocal('scroll_positions', {});
    scrollData[key] = {
      position,
      timestamp: Date.now()
    };
    this.setLocal('scroll_positions', scrollData);
  }

  /**
   * Get scroll position for a specific screen/key
   */
  getScrollPosition(key) {
    const scrollData = this.getLocal('scroll_positions', {});
    const data = scrollData[key];
    
    // Return position if it's less than 5 minutes old
    if (data && (Date.now() - data.timestamp) < 300000) {
      return data.position;
    }
    
    return 0;
  }

  /**
   * Clear old scroll positions (older than 1 hour)
   */
  cleanupScrollPositions() {
    const scrollData = this.getLocal('scroll_positions', {});
    const now = Date.now();
    const cleaned = {};
    
    Object.keys(scrollData).forEach(key => {
      if (now - scrollData[key].timestamp < 3600000) { // 1 hour
        cleaned[key] = scrollData[key];
      }
    });
    
    this.setLocal('scroll_positions', cleaned);
  }

  // ==================== USER PREFERENCES ====================
  
  /**
   * Save user preferences
   */
  savePreferences(preferences) {
    this.setCookie('user_preferences', preferences);
    this.setLocal('user_preferences', preferences);
  }

  /**
   * Get user preferences
   */
  getPreferences() {
    return this.getCookie('user_preferences') || this.getLocal('user_preferences', {});
  }

  // ==================== WISHLIST CACHE ====================
  
  /**
   * Cache wishlist for faster loading
   */
  cacheWishlist(wishlist) {
    this.setLocal('wishlist_cache', {
      items: wishlist,
      timestamp: Date.now()
    });
  }

  /**
   * Get cached wishlist
   */
  getCachedWishlist() {
    const cache = this.getLocal('wishlist_cache');
    if (cache && (Date.now() - cache.timestamp) < 3600000) { // 1 hour
      return cache.items;
    }
    return null;
  }

  // ==================== PRODUCT CACHE ====================
  
  /**
   * Cache products for faster loading
   */
  cacheProducts(products) {
    this.setLocal('products_cache', {
      items: products,
      timestamp: Date.now()
    });
  }

  /**
   * Get cached products
   */
  getCachedProducts() {
    const cache = this.getLocal('products_cache');
    // Cache valid for 10 minutes
    if (cache && (Date.now() - cache.timestamp) < 600000) {
      return cache.items;
    }
    return null;
  }

  // ==================== SEARCH HISTORY ====================
  
  /**
   * Save search to history
   */
  addSearchHistory(query) {
    if (!query || query.trim().length < 2) return;
    
    const history = this.getLocal('search_history', []);
    const trimmedQuery = query.trim();
    
    // Remove if already exists
    const filtered = history.filter(q => q !== trimmedQuery);
    
    // Add to beginning
    filtered.unshift(trimmedQuery);
    
    // Keep only last 20
    const limited = filtered.slice(0, 20);
    
    this.setLocal('search_history', limited);
  }

  /**
   * Get search history
   */
  getSearchHistory() {
    return this.getLocal('search_history', []);
  }

  /**
   * Clear search history
   */
  clearSearchHistory() {
    this.removeLocal('search_history');
  }

  // ==================== CLEANUP ====================
  
  /**
   * Clear all app data
   */
  clearAll() {
    // Clear cookies
    const cookies = Cookies.get();
    Object.keys(cookies).forEach(key => {
      if (key.startsWith(this.cookiePrefix)) {
        Cookies.remove(key);
      }
    });
    
    // Clear localStorage
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith(this.localStoragePrefix)) {
        localStorage.removeItem(key);
      }
    });
    
    // Clear sessionStorage
    Object.keys(sessionStorage).forEach(key => {
      if (key.startsWith(this.sessionStoragePrefix)) {
        sessionStorage.removeItem(key);
      }
    });
  }
}

// Create singleton instance
const storageService = new StorageService();

// Export both the class and the instance
export { StorageService };
export default storageService;